import type { ChatMessage } from '../services/aiChat';

/** The shape the `ai-chat` edge function expects for each prior turn. */
export interface HistoryTurn {
  role: 'user' | 'model';
  text: string;
}

/**
 * Builds the multi-turn history sent to the AI.
 *
 * This existed four times, byte-identical, inside AIChatContext — once on the
 * text path and once on each of the three voice paths. Extracting it gives the
 * only unit-testable surface in an 808-line React context, and means the next
 * change to the history contract happens in one place instead of four.
 *
 * Note the role rename: the app stores `assistant`, Gemini expects `model`.
 *
 * Pure refactor as of Wave 0 — the output is identical to what the four
 * inlined copies produced. The known defects in this shape (the current turn
 * being duplicated into history, error banners and mic glyphs being replayed
 * as real turns, and the payload being uncapped) are addressed in Wave 4, and
 * the tests alongside this file pin today's behaviour until then.
 */
/**
 * Locally-added UI decoration that must not be replayed to the model.
 * Voice turns are prefixed with a microphone so the user can see which
 * messages they spoke; the model has no use for it and it costs tokens.
 */
const MIC_PREFIX = /^🎙️\s*/;

/** How many turns of history to send. */
const MAX_TURNS = 24;

export function buildHistory(messages: ChatMessage[]): HistoryTurn[] {
  return messages
    // Failure notices are ours, not the model's. Replaying them taught it that
    // it had said "⚠️ The AI assistant is busy right now" and invited it to
    // reason from that.
    .filter(m => !m.isError && !(m.role === 'assistant' && m.text.startsWith('⚠️')))
    // Every turn was resent on every message alongside a freshly rebuilt CRM
    // blob, so a long conversation grew the payload without bound. The most
    // recent turns are what the model actually needs for continuity.
    .slice(-MAX_TURNS)
    .map(m => ({
      role: m.role === 'user' ? 'user' : 'model',
      text: m.role === 'user' ? m.text.replace(MIC_PREFIX, '') : m.text,
    }));
}
